import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  MapPin,
  Clock,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/useDebounce";
import {
  ESTADO_LABEL,
  ESTADO_PILL,
  ESTADOS,
  useListProspectos,
  type EstadoProspecto,
  type ProspectosFilters,
} from "./api";
import { NuevoProspectoDialog } from "./NuevoProspectoDialog";
import { useIsMobile } from "@/hooks/useIsMobile";
import { MobileCard, MobileEmpty, MobileList, MobileMeta } from "@/components/ui/mobile-list";

function relativeTime(iso: string): string {
  const sec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (sec < 60) return "ahora";
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `hace ${d} día${d === 1 ? "" : "s"}`;
  return `hace ${Math.floor(d / 30)} mes`;
}

const TABS: { key: EstadoProspecto | "todos"; label: string }[] = [
  { key: "todos", label: "Todos" },
  ...ESTADOS.map((e) => ({ key: e, label: ESTADO_LABEL[e] })),
];

const PER_PAGE = 25;

export function ProspectosListPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<EstadoProspecto | "todos">("todos");
  const [busquedaInput, setBusquedaInput] = useState("");
  const busqueda = useDebounce(busquedaInput, 300);
  const [page, setPage] = useState(1);
  const [openNew, setOpenNew] = useState(false);
  const isMobile = useIsMobile(); // rc137 — tarjetas en móvil

  const filters = useMemo<ProspectosFilters>(() => {
    const f: ProspectosFilters = { page, size: PER_PAGE };
    if (tab !== "todos") f.estado = tab;
    if (busqueda.trim()) f.q = busqueda.trim();
    return f;
  }, [tab, busqueda, page]);

  const { data, isLoading, isFetching } = useListProspectos(filters);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Prospectos</h1>
          <p className="text-xs text-muted-foreground max-md:hidden">
            Clientes potenciales. Registra, genera el link de captura de ubicación y dale seguimiento.
          </p>
        </div>
        <button
          onClick={() => setOpenNew(true)}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          <Plus size={16} /> <span className="max-md:hidden">Nuevo prospecto</span><span className="md:hidden">Nuevo</span>
        </button>
      </div>

      {/* Tabs estado */}
      {/* rc137 — móvil: tira scrolleable sin wrap */}
      <div className="flex flex-wrap items-center gap-1 border-b max-md:flex-nowrap max-md:overflow-x-auto max-md:[&>button]:shrink-0 max-md:[scrollbar-width:none]">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={cn(
              "border-b-2 px-3 py-2 text-sm transition-colors -mb-px",
              tab === t.key
                ? "border-primary font-medium text-foreground"
                : "border-transparent text-muted-foreground hover:text-foreground",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Búsqueda */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={busquedaInput}
          onChange={(e) => {
            setBusquedaInput(e.target.value);
            setPage(1);
          }}
          placeholder="Buscar por folio, nombre o teléfono…"
          className="w-full rounded-md border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {/* rc137 — móvil: tarjetas */}
      {isMobile ? (
        isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Cargando…</div>
        ) : items.length === 0 ? (
          <MobileEmpty>Sin prospectos todavía. Crea el primero con “Nuevo”.</MobileEmpty>
        ) : (
          <MobileList>
            {items.map((p) => (
              <MobileCard
                key={p.id}
                to={`/prospectos/${p.id}`}
                title={p.nombre_completo || <span className="italic text-muted-foreground">Sin nombre</span>}
                subtitle={<span className="font-mono">{p.folio}</span>}
                right={
                  <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", ESTADO_PILL[p.estado])}>
                    {ESTADO_LABEL[p.estado]}
                  </span>
                }
              >
                <MobileMeta label="Teléfono" mono>{p.telefono || "—"}</MobileMeta>
                <MobileMeta label="Ubicación">
                  {p.lat !== null && p.lng !== null ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><MapPin size={12} /> Capturada</span>
                  ) : "—"}
                </MobileMeta>
                <MobileMeta label="Registrado" span>
                  <span className="inline-flex items-center gap-1"><Clock size={12} /> {relativeTime(p.creado_at)}</span>
                </MobileMeta>
              </MobileCard>
            ))}
          </MobileList>
        )
      ) : (
      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Folio</th>
              <th className="px-3 py-2 text-left font-medium">Nombre</th>
              <th className="px-3 py-2 text-left font-medium">Teléfono</th>
              <th className="px-3 py-2 text-left font-medium">Estado</th>
              <th className="px-3 py-2 text-left font-medium">Ubicación</th>
              <th className="px-3 py-2 text-left font-medium">Registrado</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">Cargando…</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">
                  <User size={28} className="mx-auto mb-2 opacity-40" />
                  Sin prospectos todavía. Crea el primero con “Nuevo prospecto”.
                </td>
              </tr>
            ) : (
              items.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => navigate(`/prospectos/${p.id}`)}
                  className="cursor-pointer hover:bg-muted/40"
                >
                  <td className="px-3 py-2 font-mono text-xs">{p.folio}</td>
                  <td className="px-3 py-2">
                    {p.nombre_completo || <span className="text-muted-foreground italic">Sin nombre</span>}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{p.telefono || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={cn("rounded-full px-2 py-0.5 text-xs font-medium", ESTADO_PILL[p.estado])}>
                      {ESTADO_LABEL[p.estado]}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {p.lat !== null && p.lng !== null ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                        <MapPin size={12} /> Capturada
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Clock size={12} /> {relativeTime(p.creado_at)}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Paginación */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{total} prospecto{total === 1 ? "" : "s"}{isFetching ? " · actualizando…" : ""}</span>
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded border p-1 disabled:opacity-40 hover:bg-muted"
          >
            <ChevronLeft size={16} />
          </button>
          <span>{page} / {totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded border p-1 disabled:opacity-40 hover:bg-muted"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <NuevoProspectoDialog open={openNew} onClose={() => setOpenNew(false)} />
    </div>
  );
}
